import {
  Button,
  ButtonProps,
  Checkbox,
  CheckboxProps,
  Flex,
  FlexProps,
  Modal,
  ModalBody,
  ModalBodyProps,
  ModalCloseButton,
  ModalContent,
  ModalContentProps,
  ModalFooter,
  ModalHeader,
  ModalHeaderProps,
  ModalOverlay,
  ModalProps,
  SimpleGrid,
  SimpleGridProps,
  Text,
  TextProps,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";

export interface SeatTypeOption {
  value: string;
  label: string;
}

export interface SeatTypeSelectionModalProps {
  modalProps?: ModalProps;
  modalContentProps?: ModalContentProps;
  modalHeaderProps?: ModalHeaderProps;
  modalBodyProps?: ModalBodyProps;
  modalDescriptionProps?: TextProps;
  modalClearButtonProps?: ButtonProps;
  modalEmptyStateTextProps?: TextProps;
  modalGridProps?: SimpleGridProps;
  optionCardProps?: FlexProps;
  optionLabelProps?: TextProps;
  optionCheckboxProps?: CheckboxProps;
  modalApplyButtonProps?: ButtonProps;
}

function SeatTypeSelectionModal({
  isOpen,
  onClose,
  seatTypes,
  appliedSeatTypes,
  onApply,
  modalProps,
  modalContentProps,
  modalHeaderProps,
  modalBodyProps,
  modalDescriptionProps,
  modalClearButtonProps,
  modalEmptyStateTextProps,
  modalGridProps,
  optionCardProps,
  optionLabelProps,
  optionCheckboxProps,
  modalApplyButtonProps,
}: {
  isOpen: boolean;
  onClose: () => void;
  seatTypes: SeatTypeOption[];
  appliedSeatTypes: string[];
  onApply: (selectedSeatTypes: string[]) => void;
} & SeatTypeSelectionModalProps) {
  const [selectedSeatTypes, setSelectedSeatTypes] =
    useState<string[]>(appliedSeatTypes);

  useEffect(() => {
    if (isOpen) setSelectedSeatTypes(appliedSeatTypes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const toggleSeatType = (value: string) => {
    setSelectedSeatTypes((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value],
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered {...modalProps}>
      <ModalOverlay />
      <ModalContent {...modalContentProps}>
        <ModalHeader pb={1} {...modalHeaderProps}>
          Select Seat Types
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody {...modalBodyProps}>
          <Flex justify="space-between" align="center" mb={4}>
            <Text color="gray.500" fontSize="sm" {...modalDescriptionProps}>
              Choose the seat type(s) you&apos;d like to see in the seat map.
            </Text>
            {selectedSeatTypes.length > 0 && (
              <Button
                variant="link"
                size="sm"
                colorScheme="primary"
                onClick={() => setSelectedSeatTypes([])}
                {...modalClearButtonProps}
              >
                Clear
              </Button>
            )}
          </Flex>

          {seatTypes.length === 0 ? (
            <Text color="gray.500" fontSize="sm" {...modalEmptyStateTextProps}>
              No seat type information is available for this show.
            </Text>
          ) : (
            <SimpleGrid
              columns={{ base: 1, sm: 2 }}
              spacing={3}
              {...modalGridProps}
            >
              {seatTypes.map((seatType) => {
                const isChecked = selectedSeatTypes.includes(seatType.value);
                return (
                  <Flex
                    key={seatType.value}
                    as="label"
                    htmlFor={`seat-type-${seatType.value}`}
                    align="center"
                    justify="space-between"
                    gap={2}
                    borderWidth="2px"
                    borderRadius="xl"
                    borderColor={isChecked ? "primary.500" : "gray.200"}
                    bg={isChecked ? "primary.50" : "transparent"}
                    px={4}
                    py={3}
                    cursor="pointer"
                    transition="all 0.15s ease-in-out"
                    _hover={{
                      borderColor: isChecked ? "primary.500" : "primary.300",
                    }}
                    {...optionCardProps}
                  >
                    <Text
                      fontWeight={isChecked ? 600 : 500}
                      fontSize="sm"
                      noOfLines={1}
                      {...optionLabelProps}
                    >
                      {seatType.label}
                    </Text>
                    <Checkbox
                      id={`seat-type-${seatType.value}`}
                      colorScheme="primary"
                      isChecked={isChecked}
                      onChange={() => toggleSeatType(seatType.value)}
                      {...optionCheckboxProps}
                    />
                  </Flex>
                );
              })}
            </SimpleGrid>
          )}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button
            backgroundColor="primary.500"
            onClick={() => {
              onApply(selectedSeatTypes);
              onClose();
            }}
            {...modalApplyButtonProps}
          >
            {selectedSeatTypes.length > 0
              ? `Show ${selectedSeatTypes.length} Seat Type${selectedSeatTypes.length > 1 ? "s" : ""}`
              : "Show All Seats"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default SeatTypeSelectionModal;
